import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import { useAuth } from '../../components/Auth/AuthContext';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import {
  Tournament,
  Person,
  ParticipantDto,
  TournamentClient,
  PersonClient,
  ParticipantClient
} from '../../utils/apiClient';
import { ReactSelectDefault } from '../../common/components/ReactSelectDefault/ReactSelect';
import { SelectOption } from '../../common/types';
import {
  formatAgeCategoryLabel,
  formatWeightCategoryLabel,
  resolveAgeCategory,
  resolveWeightCategory,
} from '../../utils/categoryResolver';

export const AdminParticipantRegister = () => {
  const appContext = React.useContext(AppContext);
  const { id: tournamentId } = useParams<{ id: string }>();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [athletes, setAthletes] = useState<Person[]>([]);
  const [coaches, setCoaches] = useState<Person[]>([]);
  const [registeredParticipants, setRegisteredParticipants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const tournamentClient = new TournamentClient(ApiUrl, axios);
  const personClient = new PersonClient(ApiUrl, axios);
  const participantClient = new ParticipantClient(ApiUrl, axios);

  const [formData, setFormData] = useState<ParticipantDto>(new ParticipantDto({
    personId: 0,
    tournamentId: parseInt(tournamentId!),
    weight: 0,
    organization: '',
    coachId: undefined,
    rank: ''
  }));

  const [selectedWeightCategoryId, setSelectedWeightCategoryId] = useState<number | undefined>(undefined);

  // Перенаправляем, если не админ
  useEffect(() => {
    if (!isAdmin()) {
      navigate(`/tournaments/${tournamentId}`);
    }
  }, [isAdmin, navigate, tournamentId]);

  // Загрузка необходимых данных при загрузке компонента
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Загрузка данных о турнире
        const tournamentData = await tournamentClient.getTournament(parseInt(tournamentId!));
        setTournament(tournamentData);

        // Загрузка списка участников
        const participantsData = await participantClient.getParticipants(parseInt(tournamentId!));
        setRegisteredParticipants(participantsData);

        // Загрузка списка всех людей
        const personsData = await personClient.getPersons();

        // Фильтрация спортсменов
        const athletesList = personsData.filter(person =>
          person.personRoles?.some(role => role.role?.name === 'Спортсмен')
        );

        // Фильтрация тренеров
        const coachesList = personsData.filter(person =>
          person.personRoles?.some(role => role.role?.name === 'Тренер')
        );

        setAthletes(athletesList);
        setCoaches(coachesList);
      } catch (ex) {
        handleError(appContext!.setNotificationData, ex);
      } finally {
        setLoading(false);
      }
    };

    if (tournamentId) {
      fetchData();
    }
  }, [tournamentId]);

  // Проверка, зарегистрирован ли спортсмен уже на турнире
  const isAthleteRegistered = (personId: number) => {
    return registeredParticipants.some(p => p.personId === personId);
  };

  // Вычисление возраста на основе даты рождения к дате начала турнира
  const calculateAgeAtTournament = (birthDate: Date) => {
    if (!tournament) return 0;
    const targetDate = new Date(tournament.startDate);
    let age = targetDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = targetDate.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && targetDate.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Автоматическое определение возрастной категории при изменении веса/спортсмена/загрузке турнира
  useEffect(() => {
    if (!tournament || !formData.weight || formData.weight <= 0) return;

    // весовая категория
    const matchingWeightCategory = resolveWeightCategory(formData.weight, tournament.weightCategories ?? []);
    setSelectedWeightCategoryId(matchingWeightCategory?.id);
  }, [formData.weight, tournament]);

  useEffect(() => {
    if (!tournament || !formData.personId) return;
    const athlete = athletes.find(a => a.id === formData.personId);
    if (!athlete) return;

    const age = calculateAgeAtTournament(new Date(athlete.birthDate));
    const matchingAgeCategory = resolveAgeCategory(age, tournament.ageCategories ?? []);

    setFormData(new ParticipantDto({
      ...formData,
      ageCategoryInTournamentId: matchingAgeCategory?.id
    }));
  }, [formData.personId, tournament]);

  // Обработка изменений полей формы
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'weight') {
      const weight = value === '' ? 0 : parseFloat(value);
      setFormData(new ParticipantDto({
        ...formData,
        weight
      }));
    } else if (name === 'organization') {
      setFormData(new ParticipantDto({
        ...formData,
        organization: value
      }));
    } else if (name === 'rank') {
      setFormData(new ParticipantDto({
        ...formData,
        rank: value
      }));
    }
  };

  // Отправка формы
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Проверка валидности формы
    if (!formData.personId) {
      setError('Необходимо выбрать спортсмена');
      return;
    }

    if (formData.weight <= 0) {
      setError('Вес должен быть больше нуля');
      return;
    }

    try {
      // Проверка, не зарегистрирован ли уже спортсмен на этом турнире
      if (isAthleteRegistered(formData.personId)) {
        setError('Этот спортсмен уже зарегистрирован на данном турнире');
        return;
      }

      // Отправка запроса на регистрацию
      await participantClient.createParticipant(formData);

      // Обновление списка зарегистрированных участников
      const updatedParticipants = await participantClient.getParticipants(parseInt(tournamentId!));
      setRegisteredParticipants(updatedParticipants);

      // Очистка формы после успешной регистрации
      setFormData(new ParticipantDto({
        personId: 0,
        tournamentId: parseInt(tournamentId!),
        weight: 0,
        organization: '',
        coachId: undefined,
        rank: ''
      }));
      setSelectedWeightCategoryId(undefined);

      setSuccess('Участник успешно зарегистрирован');

      // Сброс сообщения об успехе через 3 секунды
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (ex) {
      // eslint-disable-next-line
      console.log(ex);
      handleError(appContext!.setNotificationData, ex);
    }
  };

  if (loading) {
    return <div>Загрузка данных...</div>;
  }

  if (!tournament) {
    return <div>Турнир не найден</div>;
  }

  return (
    <div className="admin-participant-register">
      <h2>Администрирование участников турнира: {tournament.name}</h2>

      <div className="tournament-info">
        <p><strong>Место проведения:</strong> {tournament.location}</p>
        <p><strong>Даты:</strong> {new Date(tournament.startDate).toLocaleDateString()} - {new Date(tournament.endDate).toLocaleDateString()}</p>
        <p><strong>Окончание регистрации:</strong> {new Date(tournament.registrationEndDate).toLocaleDateString()}</p>
        <p className="tournament-info__hint">
          Дозаявить участника можно и после старта турнира — до первого поединка в его группе.
        </p>
      </div>

      <div className="registration-section">
        <h3>Добавление нового участника</h3>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Спортсмен:</label>
            <ReactSelectDefault<number>
              options={athletes
                .filter(athlete => !isAthleteRegistered(athlete.id!))
                .map(athlete => {
                  const birth = new Date(athlete.birthDate);
                  const target = new Date(tournament.startDate);
                  let age = target.getFullYear() - birth.getFullYear();
                  const monthDiff = target.getMonth() - birth.getMonth();
                  if (monthDiff < 0 || (monthDiff === 0 && target.getDate() < birth.getDate())) age--;
                  return {
                    value: athlete.id!,
                    label: `${athlete.lastName} ${athlete.firstName} ${athlete.patronymic || ''} (${age} лет)`
                  };
                })}
              value={formData.personId || undefined}
              onChange={(option) => setFormData(new ParticipantDto({
                ...formData,
                personId: option.value
              }))}
              placeholder="Выберите спортсмена"
              params={{ width: 100, height: 42 }}
              isClearable
              error={false}
            />
          </div>

          <div className="form-group">
            <label>Вес (кг):</label>
            <input
              type="number"
              name="weight"
              min="0"
              max="200"
              step="0.1"
              value={formData.weight || ''}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Спортивная школа:</label>
            <input
              type="text"
              name="organization"
              value={formData.organization}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Тренер:</label>
            <ReactSelectDefault<number>
              options={coaches.map(coach => ({
                value: coach.id!,
                label: `${coach.lastName} ${coach.firstName} ${coach.patronymic || ''}`
              }))}
              value={formData.coachId}
              onChange={(option) => setFormData(new ParticipantDto({
                ...formData,
                coachId: option.value || undefined
              }))}
              placeholder="Нет тренера"
              params={{ width: 100, height: 42 }}
              isClearable
              error={false}
            />
          </div>

          <div className="form-group">
            <label>Разряд (необязательно):</label>
            <input
              type="text"
              name="rank"
              value={formData.rank || ''}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Возрастная категория:</label>
            <ReactSelectDefault<number>
              options={tournament?.ageCategories?.map(ac => ({
                value: ac.id!,
                label: formatAgeCategoryLabel(ac, tournament.ageCategories ?? [])
              })) || []}
              value={formData.ageCategoryInTournamentId}
              onChange={(option) => setFormData(new ParticipantDto({
                ...formData,
                ageCategoryInTournamentId: option.value || undefined
              }))}
              placeholder="Не удалось определить категорию"
              params={{ width: 100, height: 42 }}
              isClearable={false}
              disabled
              error={false}
            />
          </div>

          <div className="form-group">
            <label>Весовая категория:</label>
            <ReactSelectDefault<number>
              options={tournament?.weightCategories?.map(wc => ({
                value: wc.id!,
                label: formatWeightCategoryLabel(wc, tournament.weightCategories ?? [])
              })) || []}
              value={selectedWeightCategoryId}
              onChange={(option) => setSelectedWeightCategoryId(option.value || undefined)}
              placeholder="Не удалось определить категорию"
              params={{ width: 100, height: 42 }}
              isClearable={false}
              disabled
              error={false}
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn--register">Зарегистрировать</button>
            <button type="button" className="btn btn--back" onClick={() => navigate(`/tournaments/${tournamentId}`)}>Отмена</button>
          </div>
        </form>
      </div>

      <div className="participants-list-section">
        <h3>Зарегистрированные участники ({registeredParticipants.length})</h3>

        {registeredParticipants.length === 0 ? (
          <p>Нет зарегистрированных участников</p>
        ) : (
          <div className="participants-table">
            <table>
              <thead>
                <tr>
                  <th>ФИО</th>
                  <th>Возраст</th>
                  <th>Вес</th>
                  <th>Спортивная школа</th>
                  <th>Тренер</th>
                  <th>Разряд</th>
                </tr>
              </thead>
              <tbody>
                {registeredParticipants.map(participant => {
                  const age = participant.person ?
                    calculateAgeAtTournament(new Date(participant.person.birthDate)) :
                    'Н/Д';

                  return (
                    <tr key={participant.id}>
                      <td>
                        {participant.person ?
                          `${participant.person.lastName} ${participant.person.firstName} ${participant.person.patronymic || ''}` :
                          'Неизвестный спортсмен'}
                      </td>
                      <td>{age} лет</td>
                      <td>{participant.weight} кг</td>
                      <td>{participant.organization}</td>
                      <td>
                        {participant.coach ?
                          `${participant.coach.lastName} ${participant.coach.firstName}` :
                          'Не указан'}
                      </td>
                      <td>{participant.rank || 'Н/Д'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="action-buttons">
        <button onClick={() => navigate(`/tournaments/${tournamentId}`)} className="btn btn--back">
          Вернуться к турниру
        </button>
      </div>
    </div>
  );
};

export default AdminParticipantRegister;
